import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Loader2, Plus, Search } from 'lucide-react';
import CustomFieldsService from '../../services/customFields/customFieldsService';
import type { JiraField } from '../types';

export interface RegisteredField {
  id: number;
  jiraFieldId: string;
  displayName: string;
  fieldKind: string;
}

interface Props {
  /** Currently selected Jira field id (e.g. `customfield_10039`). Empty = none. */
  value: string;
  /** Called when the user picks a different field or clears the selection. */
  onChange: (jiraFieldId: string) => void;
  /**
   * Fields the parent already knows about. Used as the initial "Registered"
   * list while the picker also fetches the latest catalog from the server —
   * so freshly-registered fields from sibling sections (e.g. Forecast custom
   * fields) become visible without forcing a page reload.
   */
  registeredFields: RegisteredField[];
  /**
   * When true the picker fetches the full list of Jira fields and offers a
   * "+ Register new field" row for anything not yet registered. The new field
   * is registered with `fieldKind='string'` (same as the minimal path in
   * Forecast — the Preview / recommend flow still lives in the Forecast
   * panel). After registration the value is selected.
   */
  allowAdd?: boolean;
  placeholder?: string;
  /** Optional label shown above the field — omit to render bare. */
  label?: string;
  /** Description/help text shown under the picker. */
  description?: React.ReactNode;
  /** Optional extra option label shown at the top of the list (e.g. "— None —"). */
  clearOptionLabel?: string;
  /**
   * Fired after the user successfully registers a brand-new Jira field via
   * the picker's "Register + select" row. Lets the parent refresh its own
   * cached `registeredFields` if it wants to.
   */
  onRegistered?: (entry: RegisteredField) => void;
  /**
   * When set, restricts both the "Registered" and "Other Jira fields" lists
   * to Jira fields whose `schema.type` equals this value (e.g. `'user'` for
   * user-picker fields). The currently-selected value is always shown in the
   * trigger even if it no longer matches (so a configured field stays
   * visible). The picker fetches `/api/jira/fields` on open to compute the
   * allowed set when this is set.
   */
  filterBySchemaType?: string;
}

/**
 * Shared Jira custom-field picker. Mirrors the searchable dropdown used in
 * `ForecastCustomFieldsSection` / `CommitmentSourceConfigSection` so the
 * Quality settings panel gets the same UX: type to filter, shows the Jira
 * `customfield_XXXXX` id next to the display name, and can register a new
 * field on the fly when `allowAdd` is on.
 */
export const JiraFieldPicker: React.FC<Props> = ({
  value,
  onChange,
  registeredFields,
  allowAdd = false,
  placeholder = 'Select a Jira field…',
  label,
  description,
  clearOptionLabel,
  onRegistered,
  filterBySchemaType,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [jiraFields, setJiraFields] = useState<JiraField[]>([]);
  const [loadingJiraFields, setLoadingJiraFields] = useState(false);
  /**
   * Catalog fetched directly by the picker. Merged with `registeredFields`
   * (the parent's snapshot) so a field registered in a sibling section —
   * e.g. Forecast custom fields above on the same Settings page — appears
   * here the moment the dropdown is reopened, without forcing a page
   * reload of the whole Quality settings panel.
   */
  const [extraRegistered, setExtraRegistered] = useState<RegisteredField[]>([]);
  const [registerBusy, setRegisterBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  /**
   * Authoritative registered list = parent's snapshot ∪ live catalog the
   * picker fetched itself, deduplicated by `jiraFieldId`. Either source is
   * sufficient on its own; together they cover the cross-section case the
   * user hit ("I added Bug Type in Forecast section but it doesn't show up
   * here").
   */
  const mergedRegistered = useMemo<RegisteredField[]>(() => {
    const byId = new Map<string, RegisteredField>();
    for (const f of registeredFields) byId.set(f.jiraFieldId, f);
    for (const f of extraRegistered) {
      if (!byId.has(f.jiraFieldId)) byId.set(f.jiraFieldId, f);
    }
    return Array.from(byId.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [registeredFields, extraRegistered]);

  const selected = useMemo(
    () => mergedRegistered.find((f) => f.jiraFieldId === value) || null,
    [mergedRegistered, value]
  );

  const refreshCatalog = useCallback(async () => {
    try {
      const catalog = await CustomFieldsService.getCatalog();
      const mapped: RegisteredField[] = catalog.map((c) => ({
        id: c.id,
        jiraFieldId: c.jiraFieldId,
        displayName: c.displayName,
        fieldKind: c.fieldKind,
      }));
      setExtraRegistered(mapped);
    } catch {
      // Silent — picker still works on the parent's snapshot.
    }
  }, []);

  // Mount + on every dropdown open: refresh from the catalog so the user
  // sees the latest registered fields. Cheap GET, intentionally not awaited
  // for UI gating.
  useEffect(() => {
    void refreshCatalog();
  }, [refreshCatalog]);

  useEffect(() => {
    if (!open) return;
    void refreshCatalog();
  }, [open, refreshCatalog]);

  useEffect(() => {
    if (!open) return;
    // When a schema-type filter is active we need the full Jira field list to
    // compute the allowed ids, even without `allowAdd` (which is the path for
    // the role-field pickers — they don't offer on-the-fly registration).
    if (!filterBySchemaType && !allowAdd) return;
    if (jiraFields.length > 0) return;
    let cancelled = false;
    (async () => {
      setLoadingJiraFields(true);
      try {
        const list = await CustomFieldsService.getJiraFields();
        if (!cancelled) setJiraFields(list);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load Jira fields');
        }
      } finally {
        if (!cancelled) setLoadingJiraFields(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, allowAdd, filterBySchemaType, jiraFields.length]);

  // Close on click outside.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const el = rootRef.current;
      if (el && !el.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const q = search.trim().toLowerCase();
  // Allowed-id set when a schema-type filter is active. Includes the current
  // value so a configured field that no longer matches (e.g. Jira changed its
  // schema) still appears in the trigger and the list — the user can clear it
  // via clearOptionLabel but it never silently vanishes.
  const allowedIds = useMemo<Set<string> | null>(() => {
    if (!filterBySchemaType) return null;
    const set = new Set<string>();
    for (const f of jiraFields) {
      const schema = f.schema as { type?: unknown } | null;
      if (schema && schema.type === filterBySchemaType) set.add(f.id);
    }
    if (value) set.add(value);
    return set;
  }, [filterBySchemaType, jiraFields, value]);

  const filteredRegistered = mergedRegistered.filter((f) => {
    if (allowedIds && !allowedIds.has(f.jiraFieldId)) return false;
    if (!q) return true;
    return f.displayName.toLowerCase().includes(q) || f.jiraFieldId.toLowerCase().includes(q);
  });
  const registeredIds = new Set(mergedRegistered.map((f) => f.jiraFieldId));
  const unregistered = allowAdd
    ? jiraFields.filter((f) => {
        if (registeredIds.has(f.id)) return false;
        if (allowedIds && !allowedIds.has(f.id)) return false;
        if (!q) return true;
        return f.name.toLowerCase().includes(q) || f.id.toLowerCase().includes(q);
      })
    : [];

  const handlePick = (jiraFieldId: string) => {
    onChange(jiraFieldId);
    setOpen(false);
  };

  const handleRegister = async (field: JiraField) => {
    setRegisterBusy(field.id);
    setError(null);
    try {
      const entry = await CustomFieldsService.addCatalogEntry({
        jiraFieldId: field.id,
        displayName: field.name,
        fieldKind: 'string',
      });
      const registered: RegisteredField = {
        id: entry.id,
        jiraFieldId: entry.jiraFieldId,
        displayName: entry.displayName,
        fieldKind: entry.fieldKind,
      };
      // Optimistically push into the picker's own list so the user sees the
      // field as "Registered" immediately — refreshCatalog will reconcile
      // shortly after.
      setExtraRegistered((prev) =>
        prev.some((p) => p.jiraFieldId === registered.jiraFieldId) ? prev : prev.concat(registered)
      );
      onChange(field.id);
      onRegistered?.(registered);
      setOpen(false);
      void refreshCatalog();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to register field');
    } finally {
      setRegisterBusy(null);
    }
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      {label && (
        <label style={{ display: 'block', fontSize: 13, marginBottom: 4, fontWeight: 500 }}>
          {label}
        </label>
      )}
      <div
        onClick={() => setOpen((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          borderRadius: 6,
          border: '1px solid var(--color-border, #555)',
          background: 'var(--color-bg, #121212)',
          cursor: 'pointer',
          fontSize: 13,
          minHeight: 38,
        }}
      >
        {selected ? (
          <span>
            {selected.displayName}{' '}
            <span style={{ color: 'var(--color-text-secondary, #888)', fontSize: 11 }}>
              ({selected.jiraFieldId})
            </span>
          </span>
        ) : value ? (
          <span style={{ color: 'var(--color-text-secondary, #aaa)' }}>{value}</span>
        ) : (
          <span style={{ color: 'var(--color-text-secondary, #888)' }}>{placeholder}</span>
        )}
        <ChevronDown size={14} style={{ marginLeft: 'auto' }} />
      </div>
      {description && (
        <p
          style={{
            margin: '4px 0 0',
            fontSize: 11,
            color: 'var(--color-text-secondary, #888)',
            lineHeight: 1.4,
          }}
        >
          {description}
        </p>
      )}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            borderRadius: 6,
            border: '1px solid var(--color-border, #555)',
            background: 'var(--color-bg-elevated, #1a1a2e)',
            maxHeight: 320,
            overflow: 'auto',
            zIndex: 200,
            boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
          }}
        >
          <div
            style={{
              padding: 8,
              borderBottom: '1px solid var(--color-border, #333)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Search size={12} />
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                allowAdd ? 'Search fields… (or pick one below to register)' : 'Search fields…'
              }
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: 'var(--color-text)',
                fontSize: 13,
              }}
            />
          </div>
          {clearOptionLabel && (
            <div
              onClick={() => handlePick('')}
              role="button"
              tabIndex={0}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: 13,
                borderBottom: '1px solid var(--color-border, #2a2a2a)',
                color: 'var(--color-text-secondary, #aaa)',
                fontStyle: 'italic',
              }}
            >
              {clearOptionLabel}
            </div>
          )}

          {filteredRegistered.length > 0 && (
            <div>
              <div
                style={{
                  padding: '6px 12px',
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--color-text-secondary, #888)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  background: 'rgba(255,255,255,0.04)',
                }}
              >
                Registered in Forecast catalog
              </div>
              {filteredRegistered.map((f) => (
                <div
                  key={f.id}
                  onClick={() => handlePick(f.jiraFieldId)}
                  role="button"
                  tabIndex={0}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: 13,
                    borderBottom: '1px solid var(--color-border, #2a2a2a)',
                    background: f.jiraFieldId === value ? 'rgba(99,102,241,0.12)' : 'transparent',
                  }}
                >
                  {f.displayName}{' '}
                  <span style={{ color: 'var(--color-text-secondary, #888)', fontSize: 11 }}>
                    ({f.jiraFieldId}) · {f.fieldKind}
                  </span>
                </div>
              ))}
            </div>
          )}

          {allowAdd && (
            <div>
              <div
                style={{
                  padding: '6px 12px',
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--color-text-secondary, #888)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  background: 'rgba(255,255,255,0.04)',
                }}
              >
                Other Jira fields{' '}
                {loadingJiraFields ? (
                  <span style={{ textTransform: 'none', fontWeight: 400 }}>
                    · <Loader2 className="animate-spin" size={10} /> loading
                  </span>
                ) : null}
              </div>
              {loadingJiraFields && unregistered.length === 0 ? (
                <div
                  style={{
                    padding: 12,
                    fontSize: 12,
                    color: 'var(--color-text-secondary, #888)',
                  }}
                >
                  Loading Jira fields…
                </div>
              ) : unregistered.length === 0 ? (
                <div
                  style={{
                    padding: 12,
                    fontSize: 12,
                    color: 'var(--color-text-secondary, #888)',
                  }}
                >
                  No other Jira fields found.
                </div>
              ) : (
                unregistered.map((f) => (
                  <div
                    key={f.id}
                    style={{
                      padding: '8px 12px',
                      borderBottom: '1px solid var(--color-border, #2a2a2a)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 13,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      {f.name}{' '}
                      <span
                        style={{
                          color: 'var(--color-text-secondary, #888)',
                          fontSize: 11,
                        }}
                      >
                        ({f.id})
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRegister(f)}
                      disabled={registerBusy === f.id}
                      title="Register this Jira field in the Forecast catalog and select it."
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '4px 8px',
                        borderRadius: 4,
                        border: '1px solid var(--color-border, #555)',
                        background: 'transparent',
                        color: 'var(--color-text-secondary, #aaa)',
                        cursor: registerBusy === f.id ? 'wait' : 'pointer',
                        fontSize: 11,
                      }}
                    >
                      {registerBusy === f.id ? (
                        <Loader2 className="animate-spin" size={12} />
                      ) : (
                        <Plus size={12} />
                      )}
                      Register + select
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {error && (
            <div
              style={{
                padding: '8px 12px',
                fontSize: 12,
                color: '#ef4444',
                background: 'rgba(239,68,68,0.08)',
              }}
              role="status"
            >
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
